/**
 * Helper function to properly encode file paths for URLs
 * Encodes each path segment separately so '/' remains as '/'
 */
export const encodeFilePath = (filePath: string): string => {
  return filePath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')
}

/**
 * Helper function to fix incorrectly encoded URLs
 * Handles cases where path separators are encoded or double-encoded
 */
export const fixUrlEncoding = (url: string): string => {
  if (!url) return url

  // If it's already a full URL, check if it needs fixing
  if (url.startsWith('http')) {
    // Remove query parameters (like ?download=...)
    const [baseUrl] = url.split('?')

    // Extract the path part after 'group-messages/'
    const pathMatch = baseUrl.match(/group-messages\/(.+)$/)
    if (pathMatch) {
      const pathPart = pathMatch[1]
      // Check if path separators are encoded (indicating incorrect encoding)
      if (pathPart.includes('%2F') || pathPart.includes('%2520')) {
        // Decode to get back to original path
        // Handle multiple levels of encoding
        let decoded = pathPart
        let previousDecoded = ''
        let decodeAttempts = 0
        const maxDecodeAttempts = 10 // Prevent infinite loops

        // Keep decoding until no more changes occur or we hit max attempts
        while (decoded !== previousDecoded && decodeAttempts < maxDecodeAttempts) {
          previousDecoded = decoded
          try {
            decoded = decodeURIComponent(decoded)
            decodeAttempts++
          } catch (_error) {
            // If decoding fails, use the last successful decode
            decoded = previousDecoded
            break
          }
        }

        // Re-encode properly (only encode segments, not path separators)
        const properlyEncoded = encodeFilePath(decoded)
        return baseUrl.replace(/group-messages\/.+$/, `group-messages/${properlyEncoded}`)
      }
    }
    return baseUrl
  }

  return url
}

/**
 * Helper function to get the proper URL for Supabase storage
 * Handles both full URLs and relative paths, fixing encoding issues
 */
export const getFormattedSupabaseUrl = (
  url: string,
  supabaseUrl?: string,
  bucketName = 'group-messages'
): string => {
  if (!url) return url

  // If it's already a full URL, fix encoding if needed
  if (url.startsWith('http')) {
    return fixUrlEncoding(url)
  }

  // If it's a relative path, encode it properly and prepend Supabase URL
  if (supabaseUrl) {
    // Normalize the path first (decode if needed, then re-encode properly)
    let normalizedPath = url

    // If the path is already encoded incorrectly, decode it first
    if (url.includes('%2F') || url.includes('%2520')) {
      let decoded = url
      let previousDecoded = ''
      let decodeAttempts = 0
      const maxDecodeAttempts = 10

      while (decoded !== previousDecoded && decodeAttempts < maxDecodeAttempts) {
        previousDecoded = decoded
        try {
          decoded = decodeURIComponent(decoded)
          decodeAttempts++
        } catch (_error) {
          decoded = previousDecoded
          break
        }
      }
      normalizedPath = decoded
    }

    // Encode the path properly (each segment separately)
    const encodedPath = encodeFilePath(normalizedPath)
    return `${supabaseUrl}/storage/v1/object/public/${bucketName}/${encodedPath}`
  }
  return url
}
